/**
 * Schedules Routes (Hono + OpenAPI)
 *
 * Dashboard endpoints for the schedules job monitor:
 * - GET /functions — registered function keys (select options)
 * - GET /stats — aggregated stats (stat widget)
 * - GET /recent-logs — last 10 executions (table widget)
 */

import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import type { Context, MiddlewareHandler } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'
import type { SchedulesService } from './schedules.service.js'

type C = Context<NexusEnv>

export function createSchedulesRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()

  const authMw = ctx.core.middleware['auth']
  if (!authMw) {
    throw new Error('Auth middleware is required for schedules routes')
  }
  const auth = authMw as unknown as MiddlewareHandler<NexusEnv>

  const anyObject = z.object({}).passthrough()

  // GET /functions
  const functionsRoute = createRoute({
    method: 'get',
    path: '/functions',
    tags: ['Schedules'],
    summary: 'Registered function keys for select options',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: {
        description: 'Function list',
        content: {
          'application/json': {
            schema: z.object({
              items: z.array(z.object({ key: z.string(), name: z.string() })),
              total: z.number()
            })
          }
        }
      },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(functionsRoute, ((c: C) => {
    const service = ctx.services.get<SchedulesService>('scheduler')
    const keys = service.getRegisteredFunctions()
    return c.json({
      items: keys.map(key => ({ key, name: key })),
      total: keys.length
    })
  }) as OpenApiHandler<NexusEnv>)

  // GET /stats
  const statsRoute = createRoute({
    method: 'get',
    path: '/stats',
    tags: ['Schedules'],
    summary: 'Flat dashboard stats for stat widget',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Stats', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(statsRoute, (async (c: C): Promise<Response> => {
    const { knex } = ctx.db
    const now = new Date()
    const h24ago = ctx.db.formatTimestamp(knex, new Date(now.getTime() - 24 * 60 * 60 * 1000))

    const statusCounts = await knex('schedules')
      .select('status')
      .count('* as count')
      .groupBy('status') as { status: string; count: string | number }[]
    const total = statusCounts.reduce((sum, r) => sum + Number(r.count), 0)
    const byStatus = Object.fromEntries(statusCounts.map(r => [r.status, Number(r.count)]))

    const logStats = await knex('schedule_logs')
      .where('started_at', '>=', h24ago)
      .select(
        knex.raw('COUNT(*) as total'),
        knex.raw("SUM(CASE WHEN status = 'success' THEN 1 ELSE 0 END) as successes"),
        knex.raw("SUM(CASE WHEN status = 'retrying' THEN 1 ELSE 0 END) as retries"),
        knex.raw("SUM(CASE WHEN status = 'exhausted' THEN 1 ELSE 0 END) as exhausted"),
        knex.raw('AVG(duration_ms) as avg_duration')
      )
      .first()

    const totalExecs = Number(logStats?.total ?? 0)
    const successes = Number(logStats?.successes ?? 0)
    const successRate = totalExecs > 0 ? Math.round((successes / totalExecs) * 100) : 100

    return c.json({
      total_schedules: total,
      active: (byStatus['idle'] ?? 0) + (byStatus['running'] ?? 0),
      paused: byStatus['paused'] ?? 0,
      errored: byStatus['error'] ?? 0,
      executions_24h: totalExecs,
      'success_rate_%': successRate,
      avg_duration_ms: Math.round(Number(logStats?.avg_duration ?? 0))
    })
  }) as OpenApiHandler<NexusEnv>)

  // GET /recent-logs
  const recentLogsRoute = createRoute({
    method: 'get',
    path: '/recent-logs',
    tags: ['Schedules'],
    summary: 'Last 10 schedule executions for table widget',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: {
        description: 'Recent log rows',
        content: { 'application/json': { schema: z.array(anyObject) } }
      },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(recentLogsRoute, (async (c: C): Promise<Response> => {
    const { knex } = ctx.db
    const logs = await knex('schedule_logs')
      .select(
        'schedule_logs.id',
        'schedules.name as schedule',
        'schedule_logs.started_at',
        'schedule_logs.finished_at',
        'schedule_logs.duration_ms',
        'schedule_logs.status',
        'schedule_logs.error'
      )
      .leftJoin('schedules', 'schedule_logs.schedule_id', 'schedules.id')
      .orderBy('schedule_logs.started_at', 'desc')
      .limit(10)

    const fmt = (v: unknown) => v ? new Date(v as string).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : null
    return c.json(logs.map(l => ({
      schedule: l.schedule,
      started_at: fmt(l.started_at),
      duration_ms: l.duration_ms,
      status: l.status,
      error: l.error || null
    })))
  }) as OpenApiHandler<NexusEnv>)

  return app
}
